import { IContentSection } from '../interfaces/card-info.interface';

// Long-form SEO content for /mobile-app-marketing-services, grouped by background band.
// Headings use the site convention: lowercase with <span class="gradient-text"> accents.

// Band 1 (background-1): below the hero — agency intro + ASO.
export const MARKETING_SECTIONS_INTRO: IContentSection[] = [
  {
    headingHtml: 'full-service<br>mobile app<br><span class="gradient-text">marketing</span><br><span class="gradient-text">agency</span>',
    lead: [
      'SemNexus is a full-service mobile app marketing agency helping startups, SaaS companies, and established brands acquire users, improve retention, and increase revenue. Our mobile app marketing services combine app store optimization (ASO), user acquisition, mobile advertising, influencer marketing, conversion rate optimization, and lifecycle marketing into a unified growth strategy.',
      'Unlike traditional digital marketing agencies, our team specializes exclusively in mobile applications. We understand the unique challenges of app growth, including App Store ranking factors, Google Play visibility, subscription monetization, user retention, customer acquisition costs, and attribution tracking.',
    ],
    outro: [
      'Whether you are launching a new app or scaling an established product, our app marketing services are designed to increase downloads, improve engagement, and maximize lifetime value.',
    ],
  },
  {
    headingHtml: '<span class="gradient-text">app store</span><br><span class="gradient-text">optimization</span><br>services',
    lead: [
      'App Store Optimization (ASO) is one of the most effective ways to increase organic downloads and reduce customer acquisition costs. As a leading ASO agency, SemNexus helps mobile apps improve visibility across both the Apple App Store and Google Play Store.',
      'Our app store optimization services include:',
    ],
    bullets: [
      'Keyword research and targeting',
      'Metadata optimization',
      'App title optimization',
      'Subtitle and short description optimization',
      'App category analysis',
      'Competitor ASO research',
      'Screenshot optimization',
      'App preview video optimization',
      'Conversion rate optimization',
      'App Store A/B testing',
    ],
    outro: [
      'By improving keyword rankings and increasing App Store conversion rates, ASO creates sustainable organic growth that compounds over time. Our team continuously monitors ranking performance, search visibility, and download trends to identify new opportunities for growth.',
    ],
  },
];

// Band 2 (background-2): after the intro CTA — acquisition, paid advertising, influencer.
export const MARKETING_SECTIONS_ACQUISITION: IContentSection[] = [
  {
    headingHtml: 'mobile app<br><span class="gradient-text">user acquisition</span>',
    lead: [
      'Successful apps require more than visibility. They require a predictable user acquisition engine.',
      'Our mobile app user acquisition services leverage paid advertising channels including Apple Search Ads, Google Ads, Meta Ads, TikTok Ads, Reddit Ads, influencer campaigns, and strategic partnerships to acquire high-intent users.',
      'Rather than optimizing solely for installs, we focus on the metrics that matter most:',
    ],
    bullets: [
      'Customer acquisition cost (CAC)',
      'Return on ad spend (ROAS)',
      'Subscriber growth',
      'Revenue generation',
      'Retention rates',
      'Lifetime value (LTV)',
    ],
    outro: [
      'This revenue-first approach allows clients to scale their mobile applications while maintaining profitability.',
    ],
  },
  {
    headingHtml: 'mobile app<br>advertising <span class="gradient-text">campaign</span><br><span class="gradient-text">management</span>',
    lead: [
      'Running successful mobile advertising campaigns requires more than launching ads. It requires constant testing, optimization, and data analysis.',
      'Our mobile app advertising team manages campaigns across major advertising platforms, including:',
    ],
    bullets: [
      'Apple Search Ads',
      'Google App Campaigns',
      'Facebook Ads',
      'Instagram Ads',
      'TikTok Ads',
      'Reddit Ads',
      'YouTube Ads',
    ],
    outro: [
      'We continuously test creative assets, audience segments, bidding strategies, and conversion funnels to maximize campaign performance. Through rigorous experimentation, we identify the messaging and creative concepts that drive the highest-quality installs and strongest long-term user engagement.',
    ],
  },
  {
    headingHtml: '<span class="gradient-text">influencer marketing</span> for mobile apps',
    lead: [
      'Influencer marketing has become one of the most effective app promotion strategies available. By partnering with creators who have highly engaged audiences, apps can generate trust, social proof, and organic awareness at scale.',
      'SemNexus identifies influencers based on audience quality, demographic alignment, engagement metrics, and historical campaign performance. We manage outreach, negotiations, content strategy, tracking, and reporting to ensure every influencer partnership contributes to measurable business outcomes.',
    ],
    outro: [
      'Influencer marketing campaigns can be combined with paid advertising efforts to amplify reach and create additional user-generated content for future campaigns.',
    ],
  },
];

// Band 3 (background-3): CRO, retention, and the bottom SEO block.
export const MARKETING_SECTIONS_GROWTH: IContentSection[] = [
  {
    headingHtml: 'app store<br><span class="gradient-text">conversion rate</span><br><span class="gradient-text">optimization</span>',
    lead: [
      'Generating App Store traffic is only part of the equation. Converting visitors into users is equally important.',
      'Our conversion rate optimization process focuses on improving every element of your App Store listing, including:',
    ],
    bullets: [
      'App icons',
      'Screenshots',
      'Feature graphics',
      'Preview videos',
      'Value propositions',
      'Social proof',
      'Messaging hierarchy',
    ],
    outro: [
      'Even small increases in App Store conversion rates can significantly reduce acquisition costs while increasing overall download volume.',
      'By combining App Store Optimization with conversion rate optimization, we help clients maximize the effectiveness of both organic and paid traffic sources.',
    ],
  },
  {
    headingHtml: 'mobile app<br><span class="gradient-text">retention &amp;</span><br><span class="gradient-text">growth</span><br>marketing',
    lead: [
      'Long-term success depends on retention.',
      'Acquiring users is only the beginning. Our growth marketing team works to improve onboarding experiences, increase engagement, reduce churn, and maximize user lifetime value.',
      'We help clients implement:',
    ],
    bullets: [
      'Push notification strategies',
      'Email marketing automation',
      'User segmentation',
      'Re-engagement campaigns',
      'Subscription optimization',
      'In-app messaging',
      'Behavioral analytics',
    ],
    outro: [
      'Improving retention often generates larger revenue gains than acquiring additional users, making lifecycle marketing an essential component of sustainable app growth.',
    ],
  },
  {
    headingHtml: 'why businesses choose <span class="gradient-text">semnexus</span> as their mobile app marketing agency',
    lead: [
      'Businesses choose SemNexus because we focus exclusively on mobile applications. Our team combines expertise in app store optimization, mobile app advertising, user acquisition, conversion rate optimization, influencer marketing, and retention marketing to create scalable growth systems for mobile apps.',
      'Whether your goal is increasing App Store rankings, acquiring new users, improving subscription revenue, or scaling advertising campaigns, our mobile app marketing services are designed to support every stage of the app growth lifecycle.',
    ],
    outro: [
      'From startups preparing for launch to established applications looking to accelerate growth, SemNexus provides the strategic guidance and execution necessary to compete in today’s increasingly competitive app marketplace.',
    ],
  },
];
